import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle2, XCircle, Calendar, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { PaymentAgreement, AgreementPaymentSummary } from '@/hooks/finance/usePaymentAgreements';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(dateString: string) {
  return format(parseISO(dateString), 'dd/MM/yyyy', { locale: ptBR });
}

interface OriginalAgreementDetailsProps {
  agreement: PaymentAgreement | undefined;
  summary: AgreementPaymentSummary | undefined;
  isLoading: boolean;
}

export function OriginalAgreementDetails({ 
  agreement, 
  summary, 
  isLoading 
}: OriginalAgreementDetailsProps) {
  if (!agreement) return null;
  
  const isOriginal = agreement.agreement_number === 1;
  const installmentAmount = agreement.num_installments > 0 
    ? agreement.agreed_amount / agreement.num_installments 
    : 0;
  
  return (
    <div className="border rounded-lg p-3 bg-muted/50">
      <p className="text-xs font-medium uppercase text-muted-foreground mb-2">
        {isOriginal ? 'ACORDO ORIGINAL' : `${agreement.agreement_number - 1}ª RENEGOCIAÇÃO`}
      </p>
      
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Valor:</span>{' '}
          <span className="font-medium">{formatCurrency(agreement.agreed_amount)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Estrutura:</span>{' '}
          <span className="font-medium">
            {agreement.num_installments}x de {formatCurrency(installmentAmount)}
          </span>
        </div>
        {agreement.first_due_date && (
          <div>
            <span className="text-muted-foreground">Início:</span>{' '}
            <span className="font-medium">{formatDate(agreement.first_due_date)}</span>
          </div>
        )}
      </div>
      
      {/* Summary */}
      {isLoading ? (
        <div className="mt-3 pt-3 border-t space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ) : summary && (
        <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            <span className="text-muted-foreground">Pago:</span>{' '}
            <span className="font-medium text-green-600">
              {formatCurrency(summary.paidAmount)} ({summary.paidCount})
            </span>
          </div>
          <div className="flex items-center gap-1">
            <XCircle className="h-3.5 w-3.5 text-red-600" />
            <span className="text-muted-foreground">Em aberto:</span>{' '}
            <span className="font-medium text-red-600">
              {formatCurrency(summary.pendingAmount)} ({summary.pendingCount})
            </span>
          </div>
          {summary.lastPaidDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Último pgto:</span>{' '}
              <span className="font-medium">{formatDate(summary.lastPaidDate)}</span>
            </div>
          )}
          {summary.maxDaysOverdue > 0 && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-muted-foreground">Atraso:</span>{' '}
              <span className="font-medium text-amber-600">{summary.maxDaysOverdue} dias</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
